import * as React from "react";
import {
  AppBar,
  Box,
  Card,
  CardContent,
  CssBaseline,
  IconButton,
  ThemeProvider,
  Toolbar,
  Typography,
  createTheme,
  TextField,
  Button,
  useMediaQuery,
  CardActionArea,
  useTheme,
  Checkbox,
  Dialog,
  CircularProgress,
} from "@mui/material";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import { useEventData } from "../contexts/EventDataContext";
import { getAccessibleTextColor } from "../utils/colors"; // Assuming you have a utility function for color contrast
import { getIcons } from "../utils/icon"; // Import your icon utility function
import { addDoc, collection } from "firebase/firestore";
import { db } from "../firebase/config";
import { useNavigate, useLocation } from "react-router-dom";

/** Simple inline SVGs that match the style in your mock */
const svgToUrl = (svg) => `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;

function PreviewCard({ src, accountNumber, accountName, accountBank, theme, onImageClick }) {
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [copied, setCopied] = React.useState(false);

  const copyDetails = () => {
    // const text = [
    //   accountName && `Name: ${accountName}`,
    //   accountNumber && `Account: ${accountNumber}`,
    //   accountBank && `Bank: ${accountBank}`,
    // ]
    const text = [accountNumber].filter(Boolean).join("\n");
    navigator.clipboard
      .writeText(text || "No account info")
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => { });
  };

  return (
    <Card
      elevation={0}
      sx={{
        bgcolor: "background.paper",
        boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
        width: "100%",
        maxWidth: { xs: 300, sm: 350, md: 350 },
        minHeight: { xs: 350, md: 500 },
        display: "flex",
        flexDirection: "column",
        p: 1.5,
      }}
    >
      <Box
        sx={{
          width: "100%",
          minHeight: isMobile ? 250 : 350,
          backgroundImage: `url(${src})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          borderRadius: 1,
          position: "relative",
          cursor: src ? "pointer" : "default",
        }}
        onClick={() => src && onImageClick && onImageClick()}
      />
      <CardContent
        sx={{
          textAlign: "center",
          pt: 2,
          flex: "1",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            lineHeight: 1.4,
            letterSpacing: 0.4,
            minHeight: 40,
            whiteSpace: "pre-line",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {accountName || "No message"}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: "text.primary",
          }}
        >
          {accountNumber || "No account number"}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: "text.primary",
          }}
        >
          {accountBank || "No account bank"}
        </Typography>
        {accountNumber && (
          <Button
            onClick={copyDetails}
            size="small"
            variant="outlined"
            sx={{ mt: 1, textTransform: "none", fontWeight: 600 }}
          >
            {copied ? "Copied!" : "Copy"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
export default function GiftPage() {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const isPreview = location.pathname.includes("/preview/");

  const { eventData, gotoHome, currentEventId, uploadImageToStorage } =
    useEventData();
  const [name, setName] = React.useState("");
  const [message, setMessage] = React.useState(
    "Gifting you a life full of wonderful memories and beautiful moments!"
  );

  // Default preview image
  const [image, setImage] = React.useState(
    // "https://picsum.photos/id/237/400/400"
    "https://placehold.co/400x400/FFFFFF/8A5918?text=Preview"
  );

  const themeData = eventData.theme;
  const giftData = eventData.gift || {};
  const backgroundColor = themeData.backgroundColor?.value || "#FFF7DC";
  const globalBackgroundImage = themeData.backgroundImage?.value;
  const primaryColor = themeData.mainColor?.value || "#8B4513";
  const secondaryColor = themeData.secondaryColor?.value || "#FFD700";
  const groomBrideToggle =
    giftData.giftReceiverSetup?.groomBrideToggle?.value || false; // Toggle for groom/bride selection
  const uploadSlipEnabled = giftData.formSettings?.uploadSlip?.value || false; // Toggle for upload slip
  const enableConfirmButton = giftData.confirmButton?.enabled?.value || false;
  const confirmButtonText = giftData.confirmButton?.text?.value || "Confirm";
  const iconConfirm = giftData.confirmButton?.icon?.value || "check_circle";
  const allowAnonymous =
    giftData.formSettings?.allowAnonymous?.enabled?.value || false; // Allow anonymous gifting
  const anonymousText =
    giftData.formSettings?.allowAnonymous?.text?.value || "Send as Anonymous";
  const nameFieldEnabled =
    giftData.formSettings?.nameField?.enabled?.value || false; // Name field toggle
  const nameFieldPlaceholder =
    giftData.formSettings?.nameField?.placeholder?.value || "Your Name";
  const accountNumber = giftData.accounts?.accountNumber?.value || ""; // Account number
  const accountName = giftData.accounts?.accountName?.value || ""; // Account name
  const accountBank = giftData.accounts?.accountBank?.value || ""; // Account bank
  const accImage = giftData.backgroundImage?.value || image; // Account image URL

  const [typeSelection, setTypeSelection] = React.useState("groom"); // "groom" or "bride"
  const [anonymous, setAnonymous] = React.useState(false); // Anonymous checkbox state
  const [slipFile, setSlipFile] = React.useState(null);
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState(null);
  const [submitSuccess, setSubmitSuccess] = React.useState(false);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  const [lightboxImageLoaded, setLightboxImageLoaded] = React.useState(false);

  const handleImageClick = () => {
    if (accImage) {
      setLightboxImageLoaded(false);
      setLightboxOpen(true);
    }
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
  };

  const handleDownload = () => {
    if (!accImage) return;
    const link = document.createElement("a");
    link.href = accImage;
    link.download = "account-image.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Gallery options (replace with your own PNGs if you have them)

  const [GROOM_SVG, setGroomSVG] = React.useState(
    svgToUrl(`
        <svg width="85" height="85" viewBox="0 0 85 85" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M30.0002 51.8756C28.5418 51.8756 27.3092 51.3722 26.3022 50.3652C25.2953 49.3583 24.7918 48.1256 24.7918 46.6673C24.7918 45.209 25.2953 43.9763 26.3022 42.9694C27.3092 41.9625 28.5418 41.459 30.0002 41.459C31.4585 41.459 32.6911 41.9625 33.6981 42.9694C34.705 43.9763 35.2085 45.209 35.2085 46.6673C35.2085 48.1256 34.705 49.3583 33.6981 50.3652C32.6911 51.3722 31.4585 51.8756 30.0002 51.8756ZM55.0002 51.8756C53.5418 51.8756 52.3092 51.3722 51.3022 50.3652C50.2953 49.3583 49.7918 48.1256 49.7918 46.6673C49.7918 45.209 50.2953 43.9763 51.3022 42.9694C52.3092 41.9625 53.5418 41.459 55.0002 41.459C56.4585 41.459 57.6911 41.9625 58.6981 42.9694C59.705 43.9763 60.2085 45.209 60.2085 46.6673C60.2085 48.1256 59.705 49.3583 58.6981 50.3652C57.6911 51.3722 56.4585 51.8756 55.0002 51.8756ZM42.5002 75.834C51.8057 75.834 59.6877 72.6048 66.146 66.1465C72.6043 59.6881 75.8335 51.8062 75.8335 42.5006C75.8335 40.834 75.7293 39.2194 75.521 37.6569C75.3127 36.0944 74.9307 34.584 74.3752 33.1256C72.9168 33.4729 71.4585 33.7333 70.0002 33.9069C68.5418 34.0805 67.014 34.1673 65.4168 34.1673C59.0974 34.1673 53.1252 32.8132 47.5002 30.1048C41.8752 27.3965 37.0835 23.6118 33.1252 18.7507C30.9029 24.1673 27.7259 28.8722 23.5939 32.8652C19.462 36.8583 14.6529 39.8618 9.16683 41.8757V42.5006C9.16683 51.8062 12.396 59.6881 18.8543 66.1465C25.3127 72.6048 33.1946 75.834 42.5002 75.834ZM42.5002 84.1673C36.7363 84.1673 31.3196 83.0736 26.2502 80.8861C21.1807 78.6986 16.771 75.7298 13.021 71.9798C9.271 68.2298 6.30225 63.8201 4.11475 58.7507C1.92725 53.6812 0.833496 48.2645 0.833496 42.5006C0.833496 36.7368 1.92725 31.3201 4.11475 26.2507C6.30225 21.1812 9.271 16.7715 13.021 13.0215C16.771 9.27148 21.1807 6.30273 26.2502 4.11523C31.3196 1.92773 36.7363 0.833984 42.5002 0.833984C48.264 0.833984 53.6807 1.92773 58.7502 4.11523C63.8196 6.30273 68.2293 9.27148 71.9793 13.0215C75.7293 16.7715 78.6981 21.1812 80.8856 26.2507C83.0731 31.3201 84.1668 36.7368 84.1668 42.5006C84.1668 48.2645 83.0731 53.6812 80.8856 58.7507C78.6981 63.8201 75.7293 68.2298 71.9793 71.9798C68.2293 75.7298 63.8196 78.6986 58.7502 80.8861C53.6807 83.0736 48.264 84.1673 42.5002 84.1673Z" fill="#8A4B09"/>
        </svg>
        `)
  );
  const [BRIDE_SVG, setBrideSVG] = React.useState(
    svgToUrl(`
        <svg width="99" height="93" viewBox="0 0 99 93" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M49.4997 75.5C58.8053 75.5 66.6872 72.2535 73.1456 65.7604C79.6039 59.2674 82.8331 51.4028 82.8331 42.1667C82.8331 40.0139 82.6594 37.9479 82.3122 35.9688C81.965 33.9896 81.4094 32.0625 80.6456 30.1875C78.7706 31.3681 76.7914 32.2708 74.7081 32.8958C72.6247 33.5208 70.4719 33.8333 68.2497 33.8333C64.4997 33.8333 60.9754 32.9826 57.6768 31.2813C54.3782 29.5799 51.6525 27.1667 49.4997 24.0417C47.347 27.1667 44.6213 29.5799 41.3226 31.2813C38.024 32.9826 34.4997 33.8333 30.7497 33.8333C28.5275 33.8333 26.3747 33.5208 24.2914 32.8958C22.2081 32.2708 20.2289 31.3681 18.3539 30.1875C17.59 32.0625 17.0345 33.9896 16.6872 35.9688C16.34 37.9479 16.1664 40.0139 16.1664 42.1667C16.1664 51.4028 19.4129 59.2674 25.906 65.7604C32.399 72.2535 40.2636 75.5 49.4997 75.5ZM36.9997 51.5417C38.4581 51.5417 39.6907 51.0382 40.6976 50.0313C41.7046 49.0243 42.2081 47.7917 42.2081 46.3333C42.2081 44.875 41.7046 43.6424 40.6976 42.6354C39.6907 41.6285 38.4581 41.125 36.9997 41.125C35.5414 41.125 34.3088 41.6285 33.3018 42.6354C32.2949 43.6424 31.7914 44.875 31.7914 46.3333C31.7914 47.7917 32.2949 49.0243 33.3018 50.0313C34.3088 51.0382 35.5414 51.5417 36.9997 51.5417ZM61.9997 51.5417C63.4581 51.5417 64.6907 51.0382 65.6976 50.0313C66.7046 49.0243 67.2081 47.7917 67.2081 46.3333C67.2081 44.875 66.7046 43.6424 65.6976 42.6354C64.6907 41.6285 63.4581 41.125 61.9997 41.125C60.5414 41.125 59.3088 41.6285 58.3018 42.6354C57.2949 43.6424 56.7914 44.875 56.7914 46.3333C56.7914 47.7917 57.2949 49.0243 58.3018 50.0313C59.3088 51.0382 60.5414 51.5417 61.9997 51.5417ZM8.6664 92.1667C6.23584 92.1667 4.18723 91.2639 2.52056 89.4583C0.853895 87.6528 0.124729 85.5347 0.333062 83.1042L4.08306 41.9583C4.63862 36.125 6.21848 30.6736 8.82265 25.6042C11.4268 20.5347 14.7601 16.1424 18.8226 12.4271C22.8851 8.71181 27.5379 5.79514 32.781 3.67708C38.024 1.55903 43.597 0.5 49.4997 0.5C55.4025 0.5 60.9754 1.55903 66.2185 3.67708C71.4615 5.79514 76.1143 8.71181 80.1768 12.4271C84.2393 16.1424 87.5726 20.5347 90.1768 25.6042C92.781 30.6736 94.3608 36.125 94.9164 41.9583L98.6664 83.1042C98.8747 85.5347 98.1456 87.6528 96.4789 89.4583C94.8122 91.2639 92.7636 92.1667 90.3331 92.1667H8.6664Z" fill="#8A4B09"/>
        </svg>
      `)
  );

  const [gallery, setGallery] = React.useState([
    { label: "Groom", value: "groom", src: GROOM_SVG },
    { label: "Bride", value: "bride", src: BRIDE_SVG },
  ]);

  const theme = createTheme({
    palette: {
      primary: {
        main: primaryColor,
        contrastText: getAccessibleTextColor(secondaryColor),
      },
      secondary: {
        main: secondaryColor,
        contrastText: getAccessibleTextColor(primaryColor),
      },
      background: { default: backgroundColor },
    },
    shape: { borderRadius: 16 },
  });

  React.useEffect(() => {
    const newGroomSVG = svgToUrl(
      `<svg width="85" height="85" viewBox="0 0 85 85" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M30.0002 51.8756C28.5418 51.8756 27.3092 51.3722 26.3022 50.3652C25.2953 49.3583 24.7918 48.1256 24.7918 46.6673C24.7918 45.209 25.2953 43.9763 26.3022 42.9694C27.3092 41.9625 28.5418 41.459 30.0002 41.459C31.4585 41.459 32.6911 41.9625 33.6981 42.9694C34.705 43.9763 35.2085 45.209 35.2085 46.6673C35.2085 48.1256 34.705 49.3583 33.6981 50.3652C32.6911 51.3722 31.4585 51.8756 30.0002 51.8756ZM55.0002 51.8756C53.5418 51.8756 52.3092 51.3722 51.3022 50.3652C50.2953 49.3583 49.7918 48.1256 49.7918 46.6673C49.7918 45.209 50.2953 43.9763 51.3022 42.9694C52.3092 41.9625 53.5418 41.459 55.0002 41.459C56.4585 41.459 57.6911 41.9625 58.6981 42.9694C59.705 43.9763 60.2085 45.209 60.2085 46.6673C60.2085 48.1256 59.705 49.3583 58.6981 50.3652C57.6911 51.3722 56.4585 51.8756 55.0002 51.8756ZM42.5002 75.834C51.8057 75.834 59.6877 72.6048 66.146 66.1465C72.6043 59.6881 75.8335 51.8062 75.8335 42.5006C75.8335 40.834 75.7293 39.2194 75.521 37.6569C75.3127 36.0944 74.9307 34.584 74.3752 33.1256C72.9168 33.4729 71.4585 33.7333 70.0002 33.9069C68.5418 34.0805 67.014 34.1673 65.4168 34.1673C59.0974 34.1673 53.1252 32.8132 47.5002 30.1048C41.8752 27.3965 37.0835 23.6118 33.1252 18.7507C30.9029 24.1673 27.7259 28.8722 23.5939 32.8652C19.462 36.8583 14.6529 39.8618 9.16683 41.8757V42.5006C9.16683 51.8062 12.396 59.6881 18.8543 66.1465C25.3127 72.6048 33.1946 75.834 42.5002 75.834ZM42.5002 84.1673C36.7363 84.1673 31.3196 83.0736 26.2502 80.8861C21.1807 78.6986 16.771 75.7298 13.021 71.9798C9.271 68.2298 6.30225 63.8201 4.11475 58.7507C1.92725 53.6812 0.833496 48.2645 0.833496 42.5006C0.833496 36.7368 1.92725 31.3201 4.11475 26.2507C6.30225 21.1812 9.271 16.7715 13.021 13.0215C16.771 9.27148 21.1807 6.30273 26.2502 4.11523C31.3196 1.92773 36.7363 0.833984 42.5002 0.833984C48.264 0.833984 53.6807 1.92773 58.7502 4.11523C63.8196 6.30273 68.2293 9.27148 71.9793 13.0215C75.7293 16.7715 78.6981 21.1812 80.8856 26.2507C83.0731 31.3201 84.1668 36.7368 84.1668 42.5006C84.1668 48.2645 83.0731 53.6812 80.8856 58.7507C78.6981 63.8201 75.7293 68.2298 71.9793 71.9798C68.2293 75.7298 63.8196 78.6986 58.7502 80.8861C53.6807 83.0736 48.264 84.1673 42.5002 84.1673Z" fill="${primaryColor}"/>
        </svg>`
    );
    const newBrideSVG = svgToUrl(
      `<svg width="99" height="93" viewBox="0 0 99 93" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M49.4997 75.5C58.8053 75.5 66.6872 72.2535 73.1456 65.7604C79.6039 59.2674 82.8331 51.4028 82.8331 42.1667C82.8331 40.0139 82.6594 37.9479 82.3122 35.9688C81.965 33.9896 81.4094 32.0625 80.6456 30.1875C78.7706 31.3681 76.7914 32.2708 74.7081 32.8958C72.6247 33.5208 70.4719 33.8333 68.2497 33.8333C64.4997 33.8333 60.9754 32.9826 57.6768 31.2813C54.3782 29.5799 51.6525 27.1667 49.4997 24.0417C47.347 27.1667 44.6213 29.5799 41.3226 31.2813C38.024 32.9826 34.4997 33.8333 30.7497 33.8333C28.5275 33.8333 26.3747 33.5208 24.2914 32.8958C22.2081 32.2708 20.2289 31.3681 18.3539 30.1875C17.59 32.0625 17.0345 33.9896 16.6872 35.9688C16.34 37.9479 16.1664 40.0139 16.1664 42.1667C16.1664 51.4028 19.4129 59.2674 25.906 65.7604C32.399 72.2535 40.2636 75.5 49.4997 75.5ZM36.9997 51.5417C38.4581 51.5417 39.6907 51.0382 40.6976 50.0313C41.7046 49.0243 42.2081 47.7917 42.2081 46.3333C42.2081 44.875 41.7046 43.6424 40.6976 42.6354C39.6907 41.6285 38.4581 41.125 36.9997 41.125C35.5414 41.125 34.3088 41.6285 33.3018 42.6354C32.2949 43.6424 31.7914 44.875 31.7914 46.3333C31.7914 47.7917 32.2949 49.0243 33.3018 50.0313C34.3088 51.0382 35.5414 51.5417 36.9997 51.5417ZM61.9997 51.5417C63.4581 51.5417 64.6907 51.0382 65.6976 50.0313C66.7046 49.0243 67.2081 47.7917 67.2081 46.3333C67.2081 44.875 66.7046 43.6424 65.6976 42.6354C64.6907 41.6285 63.4581 41.125 61.9997 41.125C60.5414 41.125 59.3088 41.6285 58.3018 42.6354C57.2949 43.6424 56.7914 44.875 56.7914 46.3333C56.7914 47.7917 57.2949 49.0243 58.3018 50.0313C59.3088 51.0382 60.5414 51.5417 61.9997 51.5417ZM8.6664 92.1667C6.23584 92.1667 4.18723 91.2639 2.52056 89.4583C0.853895 87.6528 0.124729 85.5347 0.333062 83.1042L4.08306 41.9583C4.63862 36.125 6.21848 30.6736 8.82265 25.6042C11.4268 20.5347 14.7601 16.1424 18.8226 12.4271C22.8851 8.71181 27.5379 5.79514 32.781 3.67708C38.024 1.55903 43.597 0.5 49.4997 0.5C55.4025 0.5 60.9754 1.55903 66.2185 3.67708C71.4615 5.79514 76.1143 8.71181 80.1768 12.4271C84.2393 16.1424 87.5726 20.5347 90.1768 25.6042C92.781 30.6736 94.3608 36.125 94.9164 41.9583L98.6664 83.1042C98.8747 85.5347 98.1456 87.6528 96.4789 89.4583C94.8122 91.2639 92.7636 92.1667 90.3331 92.1667H8.6664Z" fill="${primaryColor}"/>
        </svg>`
    );
    setGroomSVG(newGroomSVG);
    setBrideSVG(newBrideSVG);
    setGallery([
      { label: "Groom", value: "groom", src: newGroomSVG },
      { label: "Bride", value: "bride", src: newBrideSVG },
    ]);
  }, [primaryColor]);

  const onPick = (value) => setTypeSelection(value);
  const setAnonymousOnChange = (value) => {
    setAnonymous(value);
    if (value) setName("");
  };

  const [backgroundImage, setBackgroundImage] = React.useState(null);
  const handleFileChange = (e) => {
    // เอา isPreview guard ออก ให้ upload slip ได้ใน preview
    const file = e.target.files[0];
    if (!file) return;
    setSlipFile(file);
    const r = new FileReader();
    r.onloadend = () => setBackgroundImage(r.result);
    r.readAsDataURL(file);
  };
  const removeImage = () => {
    // เอา isPreview guard ออก ให้ลบ slip ได้ใน preview
    setBackgroundImage(null);
    setSlipFile(null);
    setUploadProgress(0);
  };

  const handleConfirm = async () => {
    if (isPreview) return; // บล็อคเซฟในโหมด preview เท่านั้น
    if (!currentEventId) return;
    if (nameFieldEnabled && !anonymous && !name.trim()) {
      setSubmitError("Please enter your name");
      return;
    }
    if (uploadSlipEnabled && !slipFile) {
      setSubmitError("Please upload the slip image");
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);
    try {
      let slipUrl = null;
      if (uploadSlipEnabled && slipFile) {
        const res = await uploadImageToStorage(slipFile, {
          folder: "giftSlips",
          eventId: currentEventId,
          onProgress: (p) => setUploadProgress(p),
        });
        if (res.success) slipUrl = res.url;
        else throw new Error(res.message || "Upload failed");
      }
      await addDoc(collection(db, "events", currentEventId, "gifts"), {
        eventId: currentEventId,
        type: groomBrideToggle ? typeSelection : null,
        name: anonymous ? anonymousText || "Anonymous" : name || null,
        isAnonymous: nameFieldEnabled ? anonymous : true,
        slipUrl,
        createdAt: new Date(),
      });
      setSubmitSuccess(true);
      setName("");
      setMessage("");
      removeImage();
      setAnonymous(false);
      navigate(`/${currentEventId}/gift-landing`, { replace: true });
    } catch (e) {
      setSubmitError(e.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppBar position="sticky" elevation={0}>
        <Toolbar sx={{ minHeight: 64 }}>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="back"
            onClick={gotoHome}
            sx={{ mr: 2, color: getAccessibleTextColor(primaryColor) }}
          >
            <ArrowBackIosNewRoundedIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              textAlign: "center",
              letterSpacing: 4,
              mr: 6,
              color: getAccessibleTextColor(primaryColor),
            }}
          >
            Gift
          </Typography>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          minHeight: "calc(100vh - 64px)",
          backgroundImage: globalBackgroundImage ? `url(${globalBackgroundImage})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        {/* Preview on Left */}
        <Box
          sx={{
            flex: { xs: "none", md: 1 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: { xs: 2, md: 2 },
          }}
        >
          <PreviewCard
            src={accImage}
            accountNumber={accountNumber}
            accountName={accountName}
            accountBank={accountBank}
            theme={theme}
            onImageClick={handleImageClick}
          />
        </Box>

        {/* Form on Right */}
        <Box
          sx={{
            flex: { xs: "none", md: 1 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignContent: "center",
            p: { xs: 2, md: 10 },
          }}
        >
          <Box
            sx={{
              maxWidth: "100%",
              mx: "auto",
              width: "100%",
              justifyContent: "center",
              bgcolor: globalBackgroundImage ? "rgba(255,255,255,0.8)" : "transparent",
              backdropFilter: globalBackgroundImage ? "blur(10px)" : "none",
              p: 4,
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                mb: 3,
                width: "100%",
                justifyContent: "center",
                justifyItems: "center",
                gap: 2,
              }}
            >
              {groomBrideToggle &&
                gallery.map(({ src, label, value }) => {
                  const active = typeSelection === value;
                  return (
                    <Box sx={{ textAlign: "center" }} key={value}>
                      <Card
                        elevation={0}
                        sx={{
                          borderRadius: 1,
                          border: active
                            ? `3px solid ${primaryColor}`
                            : "2px solid transparent",
                          boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                          transition: "border .2s ease, transform .08s ease",
                          "&:hover": { border: `2px solid ${primaryColor}` },
                          width: isMobile ? 125 : 175,
                          mb: 1,
                        }}
                      >
                        <CardActionArea onClick={() => onPick(value)}>
                          <Box
                            sx={{
                              p: 2,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 1,
                              bgcolor: "#fff",
                            }}
                          >
                            <Box
                              sx={{
                                width: "100%",
                                height: isMobile ? 80 : 120,
                                borderRadius: 1,
                                overflow: "hidden",
                              }}
                            >
                              <img
                                src={src}
                                alt={label}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "fill",
                                  display: "block",
                                }}
                              />
                            </Box>
                          </Box>
                        </CardActionArea>
                      </Card>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: "#8A5918",
                          fontWeight: 700,
                          letterSpacing: 1,
                        }}
                      >
                        {label}
                      </Typography>
                    </Box>
                  );
                })}
            </Box>
            {nameFieldEnabled && !anonymous && (
              <TextField
                label="Name"
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={nameFieldPlaceholder}
                sx={{
                  mb: 2.5,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: secondaryColor,
                    borderColor: primaryColor,
                  },
                }}
                disabled={anonymous}
                InputProps={{
                  endAdornment: name && !anonymous && (
                    <IconButton
                      onClick={() => setName("")}
                      disabled={anonymous}
                      size="small"
                    >
                      <CloseIcon />
                    </IconButton>
                  ),
                }}
              />
            )}

            {allowAnonymous && (
              <Box sx={{ display: "flex", alignItems: "center", mb: 2.5 }}>
                <Checkbox
                  disabled={!nameFieldEnabled}
                  checked={anonymous || nameFieldEnabled ? anonymous : true}
                  onChange={(e) => setAnonymousOnChange(e.target.checked)}
                  type="checkbox"
                  id="anonymous"
                  style={{ marginRight: 8 }}
                />
                <label
                  htmlFor="anonymous"
                  style={{ color: primaryColor, fontWeight: 600 }}
                >
                  {anonymousText || "Anonymous"}
                </label>
              </Box>
            )}

            {uploadSlipEnabled && (
              <Box
                sx={{
                  position: "relative",
                  p: 2,
                  textAlign: "center",
                  height: 300,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  borderRadius: 2,
                  mb: 3,
                  border: "2px dashed #ccc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: secondaryColor,
                }}
              >
                {backgroundImage ? (
                  <>
                    <img
                      src={backgroundImage}
                      alt="Preview"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "275px",
                        width: "auto",
                        height: "auto",
                        objectFit: "contain",
                        borderRadius: 12,
                      }}
                    />
                    {!isPreview && (
                      <IconButton
                        onClick={removeImage}
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          backgroundColor: "white",
                          border: `2px solid ${primaryColor}`,
                          "&:hover": {
                            backgroundColor: "#eee",
                          },
                        }}
                      >
                        <CloseIcon sx={{ color: primaryColor }} />
                      </IconButton>
                    )}
                    {isSubmitting && slipFile && (
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 8,
                          left: 0,
                          right: 0,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: primaryColor, fontWeight: 600 }}
                        >
                          Uploading {Math.round(uploadProgress)}%
                        </Typography>
                        <Box
                          sx={{
                            mt: 0.5,
                            height: 6,
                            bgcolor: "#fff",
                            mx: 2,
                            borderRadius: 3,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              width: `${uploadProgress}%`,
                              height: "100%",
                              bgcolor: primaryColor,
                            }}
                          />
                        </Box>
                      </Box>
                    )}
                  </>
                ) : (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    sx={{ height: 300, justifyContent: "center" }}
                  >
                    <AttachFileIcon sx={{ color: primaryColor, fontSize: 28 }} />
                    <Button
                      variant="contained"
                      component="label"
                      // เอา disabled={isPreview} ออก ให้เลือกไฟล์ได้ใน preview
                      sx={{
                        backgroundColor: primaryColor,
                        color: getAccessibleTextColor(primaryColor),
                        borderRadius: 2,
                        px: 4,
                        mt: 3,
                        fontWeight: 600,
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: primaryColor,
                        },
                        // เอา opacity จาก isPreview ออกด้วยถ้าไม่อยากให้จาง
                      }}
                    >
                      Upload Slip
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleFileChange}
                      />
                    </Button>
                  </Box>
                )}
              </Box>
            )}
            {submitError && (
              <Typography variant="body2" sx={{ color: "red", mb: 1 }}>
                {submitError}
              </Typography>
            )}
            {submitSuccess && (
              <Typography variant="body2" sx={{ color: "green", mb: 1 }}>
                Gift submitted successfully!
              </Typography>
            )}
            {enableConfirmButton && (
              <Button
                variant="contained"
                color="primary"
                fullWidth
                startIcon={getIcons(iconConfirm)}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: 16,
                  color: getAccessibleTextColor(primaryColor),
                  opacity: isSubmitting || isPreview ? 0.7 : 1,
                }}
                disabled={
                  isPreview || // บล็อคเฉพาะปุ่มเซฟใน preview
                  isSubmitting ||
                  (uploadSlipEnabled && !slipFile)
                }
                onClick={handleConfirm}
              >
                {isPreview
                  ? "Preview mode"
                  : isSubmitting
                    ? "Submitting..."
                    : confirmButtonText || "Confirm Gift"}
              </Button>
            )}
          </Box>
        </Box>
      </Box>


      {/* Lightbox Dialog */}
      <Dialog
        open={lightboxOpen}
        onClose={handleCloseLightbox}
        maxWidth="lg"
        PaperProps={{
          style: {
            backgroundColor: "transparent",
            boxShadow: "none",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            outline: "none",
            minWidth: "300px",
            minHeight: "300px",
          }}
          onClick={handleCloseLightbox}
        >
          {/* Close Button */}
          <IconButton
            onClick={handleCloseLightbox}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              bgcolor: "rgba(0, 0, 0, 0.5)",
              color: "white",
              zIndex: 10,
              "&:hover": {
                bgcolor: "rgba(0, 0, 0, 0.7)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Download Button */}
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleDownload();
            }}
            sx={{
              position: "absolute",
              bottom: 16,
              right: 16,
              bgcolor: "rgba(0, 0, 0, 0.5)",
              color: "white",
              zIndex: 10,
              "&:hover": {
                bgcolor: "rgba(0, 0, 0, 0.7)",
              },
            }}
          >
            <DownloadRoundedIcon />
          </IconButton>

          {/* Loading Indicator */}
          {!lightboxImageLoaded && (
            <CircularProgress
              size={50}
              sx={{
                color: "white",
                position: "absolute",
                zIndex: 1,
              }}
            />
          )}

          <img
            src={accImage}
            alt="Account"
            onLoad={() => setLightboxImageLoaded(true)}
            style={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              objectFit: "contain",
              borderRadius: 8,
              opacity: lightboxImageLoaded ? 1 : 0,
              transition: "opacity 0.3s ease-in-out",
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </Box>
      </Dialog>
    </ThemeProvider >
  );
}
